<div
  class="page-container min-h-0 flex-1 gap-6 overflow-hidden"
  [class.pb-24]="hasUnsavedChanges()">
  <app-page-header
    [title]="collection()?.name || 'Collection'"
    [description]="headerDescription()"
    backLabel="Data Library"
    backLink="/data-library">
    <div class="flex flex-wrap gap-2">
      <lib-signal-button
        class="collection-header-outline-btn"
        appearance="outline"
        iconSrc="assets/icons/view-column-outline.svg"
        [fullWidth]="false"
        (clicked)="openEditColumns()">
        Edit columns
      </lib-signal-button>
      <lib-signal-button
        class="collection-header-outline-btn"
        appearance="outline"
        iconSrc="assets/icons/plus.svg"
        [fullWidth]="false"
        (clicked)="addRow()">
        Add row
      </lib-signal-button>
    </div>
  </app-page-header>

  <section
    class="page-fill flex min-h-0 flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm">
    @if (collection(); as current) {
      <div class="min-h-0 flex-1 overflow-auto">
        <table class="min-w-full border-separate border-spacing-0 text-sm">
          <thead>
            <tr class="text-left">
              <th
                class="sticky top-0 left-0 z-30 w-16 min-w-16 bg-neutral-50 px-3 py-2.5 text-center font-semibold text-neutral-800 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]">
                #
              </th>
              @for (field of current.fields; track field.key) {
                <th
                  class="sticky top-0 z-20 whitespace-nowrap bg-neutral-50 px-2 py-2.5 font-semibold text-neutral-800">
                  {{ field.name }}
                  @if (isFieldRequired(field)) {
                    <span class="ml-0.5 text-red-500" aria-hidden="true">*</span>
                  } @else {
                    <span class="ml-1 text-xs font-medium text-neutral-400"
                      >(optional)</span
                    >
                  }
                  @if (current.uniqueFieldKey === field.key) {
                    <span class="ml-1 text-xs font-medium text-neutral-400"
                      >unique</span
                    >
                  }
                </th>
              }
              <th
                class="sticky top-0 z-20 w-20 rounded-r-xl bg-neutral-50 px-2 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            @if (rows().length === 0) {
              <tr>
                <td
                  class="px-2 py-10 text-center text-sm text-neutral-500"
                  [attr.colspan]="current.fields.length + 2">
                  No rows yet. Click Add row to start entering data.
                </td>
              </tr>
            }
            @for (row of rows(); track row.localId; let rowIndex = $index) {
              <tr
                class="group border-b border-neutral-100 align-middle transition-colors focus-within:bg-primary-50">
                <td
                  class="sticky left-0 z-10 w-16 min-w-16 bg-white px-3 py-2 text-center text-xs font-medium text-neutral-500 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)] group-focus-within:bg-primary-50">
                  {{ rowIndex + 1 }}
                </td>
                @for (field of current.fields; track field.key) {
                  <td class="px-2 py-2">
                    @switch (field.valueType) {
                      @case ('text') {
                        <input
                          type="text"
                          [class]="cellInputClass(row.localId, field.key, 'min-w-36')"
                          [attr.data-cell]="row.localId + ':' + field.key"
                          [value]="cellAsString(row.values[field.key])"
                          (input)="setText(row.localId, field.key, $any($event.target).value)" />
                      }
                      @case ('number') {
                        <input
                          type="number"
                          [class]="cellInputClass(row.localId, field.key, 'min-w-28')"
                          [attr.data-cell]="row.localId + ':' + field.key"
                          [value]="cellAsString(row.values[field.key])"
                          (input)="setNumber(row.localId, field.key, $any($event.target).value)" />
                      }
                      @case ('boolean') {
                        <lib-signal-checkbox
                          label=""
                          [value]="cellAsBoolean(row.values[field.key])"
                          (valueChange)="setBoolean(row.localId, field.key, $event)" />
                      }
                      @case ('date') {
                        <input
                          type="date"
                          [class]="cellInputClass(row.localId, field.key, 'min-w-36')"
                          [attr.data-cell]="row.localId + ':' + field.key"
                          [value]="cellAsString(row.values[field.key])"
                          (input)="setDate(row.localId, field.key, $any($event.target).value)" />
                      }
                    }
                  </td>
                }
                <td class="px-2 py-2">
                  <button
                    type="button"
                    class="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-neutral-200 text-red-600 transition hover:border-red-200 hover:bg-red-50"
                    aria-label="Delete row"
                    (click)="removeRow(row.localId)">
                    <app-svg-icon src="assets/icons/trash-can-outline.svg" [size]="18" />
                  </button>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <div
        class="shrink-0 border-t border-neutral-100 bg-white px-4 py-3 sm:px-6">
        <button
          type="button"
          class="flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-primary-200 bg-primary-50 px-4 py-3 text-sm font-medium text-primary-600 transition hover:border-primary-300 hover:bg-primary-100"
          (click)="addRow()">
          <svg
            class="h-5 w-5 shrink-0"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true">
            <circle
              cx="10"
              cy="10"
              r="8.25"
              stroke="currentColor"
              stroke-width="1.5" />
            <path
              d="M10 6.5v7M6.5 10h7"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round" />
          </svg>
          Add row
        </button>
      </div>
    }
  </section>

  @if (hasUnsavedChanges()) {
    <div
      class="fixed bottom-6 left-1/2 z-50 flex w-[min(36rem,calc(100%-2rem))] -translate-x-1/2 items-center justify-between gap-4 rounded-2xl bg-slate-900 px-5 py-3.5 shadow-2xl">
      <div class="flex min-w-0 items-center gap-3">
        <span
          class="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-500 text-white">
          <app-svg-icon
            class="text-white"
            src="assets/icons/information-outline.svg"
            [size]="16" />
        </span>
        <span class="truncate text-sm font-medium text-white">
          Unsaved changes
        </span>
      </div>
      <div class="flex shrink-0 items-center gap-2">
        <button
          type="button"
          class="inline-flex h-9 cursor-pointer items-center justify-center rounded-lg bg-white px-4 text-sm font-medium text-slate-900 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
          [disabled]="saving()"
          (click)="discard()">
          Discard
        </button>
        <button
          type="button"
          class="inline-flex h-9 cursor-pointer items-center justify-center rounded-lg bg-primary-600 px-4 text-sm font-medium text-white transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
          [disabled]="saving()"
          (click)="save()">
          Save
        </button>
      </div>
    </div>
  }
</div>
